import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircleIcon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react'
import {
	Alert, AlertDescription, AlertTitle,
	Button, Checkbox, ConfirmDialog, IconButton,
	Spinner,
} from '@rentflowdev/ui'
import {
	useDeleteTaskMutation,
	useListDealMeetingsQuery,
	useListTasksQuery,
	useUpdateDealMeetingMutation,
	useUpdateTaskMutation,
} from '@/api'
import { TaskFormDialog } from '@/components/task-form-dialog'
import { extractErrorMessage } from '@/lib/error-message'
import type { Task } from '@/types'
import { DEFAULT_LOCALE } from '@/types'

interface DealTasksSectionProps {
	dealId: string
	adding?: boolean
	onAddingChange?: (v: boolean) => void
	/** Переводит на вкладку «Встречи» — там встречу и правят. */
	onOpenMeetings?: () => void
}

function formatDueDate(iso: string): string {
	return new Date(iso).toLocaleDateString(DEFAULT_LOCALE, {
		day:   '2-digit',
		month: '2-digit',
		year:  '2-digit',
	})
}

// Unified tasks only (feedback #286): the deal card reads and writes the
// global Task entity with a DEAL binding — the legacy DealTask rows were
// migrated in 20260731140000_unify_tasks.
//
// Creating and editing go through the shared TaskFormDialog, the same one the
// rental card uses. The inline form this replaced offered only kind, due date
// and description, so a task made from a deal could never get a title or an
// assignee.
export function DealTasksSection({ dealId, adding: addingProp, onAddingChange, onOpenMeetings }: DealTasksSectionProps) {
	const { t } = useTranslation()
	const { data, isLoading } = useListTasksQuery({ bindingKind: 'DEAL', bindingId: dealId, limit: 50 })
	const [updateTask] = useUpdateTaskMutation()
	const [updateMeeting] = useUpdateDealMeetingMutation()
	const [deleteTask] = useDeleteTaskMutation()

	const [addingLocal, setAddingLocal] = useState(false)
	const adding    = addingProp    ?? addingLocal
	const setAdding = onAddingChange ?? setAddingLocal
	const [taskToEdit, setTaskToEdit] = useState<Task | undefined>(undefined)
	const [actionError, setActionError] = useState<{ title: string; description?: string } | null>(null)
	const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null)

	const tasks = data?.items ?? []
	/**
	 * Встречи этой сделки — теми же строками.
	 *
	 * В общем разделе «Задачи» они уже показываются, и если здесь под тем же
	 * словом их нет, слово значит разное в двух местах. Правят их на соседней
	 * вкладке, поэтому строка ведёт туда, а не открывает форму задачи: у
	 * встречи есть адрес и время, которых у задачи нет.
	 */
	const { data: meetingsData } = useListDealMeetingsQuery({ dealId })
	const meetings = (meetingsData?.items ?? []).filter((m) => m.status !== 'CANCELLED')
	const formOpen = adding || !!taskToEdit

	function closeForm() {
		setAdding(false)
		setTaskToEdit(undefined)
	}

	async function handleDelete(taskId: string) {
		setActionError(null)
		try {
			await deleteTask(taskId).unwrap()
		} catch (err) {
			setActionError({ title: t('deals.tasks.errors.delete'), description: extractErrorMessage(err) })
		}
	}

	function confirmDelete() {
		if (deleteTargetId) void handleDelete(deleteTargetId)
		setDeleteTargetId(null)
	}

	if (isLoading) {
		return <Spinner size="sm" />
	}

	return (
		<div className="flex flex-col gap-3">
			{actionError && (
				<Alert variant="destructive">
					<AlertCircleIcon className="size-4" />
					<AlertTitle>{actionError.title}</AlertTitle>
					{actionError.description && <AlertDescription>{actionError.description}</AlertDescription>}
				</Alert>
			)}
			{tasks.length === 0 && (
				<p className="text-sm text-(--color-muted-foreground)">{t('deals.tasks.empty')}</p>
			)}

			{meetings.map((m) => (
				<div
					key={m.id}
					className="flex items-center gap-3 rounded-md border border-(--color-border) bg-(--color-background) px-3 py-2"
				>
					<Checkbox
						checked={m.status === 'COMPLETED'}
						onCheckedChange={(c) => {
							// «Сделано» для встречи — её статус: снятая галочка возвращает
							// её в запланированные, а не отменяет.
							void updateMeeting({ id: m.id, status: c === true ? 'COMPLETED' : 'SCHEDULED' })
						}}
					/>
					<div className="flex flex-1 min-w-0 flex-col">
						<span className={`truncate text-sm ${m.status === 'COMPLETED' ? 'line-through text-(--color-muted-foreground)' : 'text-(--color-foreground)'}`}>
							{m.address}
						</span>
						<span className="text-xs text-(--color-muted-foreground)">
							{t('deals.tasks.types.MEETING')} • {formatDueDate(m.scheduledAt)}
						</span>
					</div>
					{onOpenMeetings && (
						<IconButton
							variant="ghost"
							size="sm"
							type="button"
							onClick={onOpenMeetings}
							aria-label={t('common.edit')}
						>
							<PencilIcon className="size-4" />
						</IconButton>
					)}
				</div>
			))}

			{tasks.map((task) => (
				<div
					key={task.id}
					className="flex items-center gap-3 rounded-md border border-(--color-border) bg-(--color-background) px-3 py-2"
				>
					<Checkbox
						checked={task.isDone}
						onCheckedChange={(c) => {
							void updateTask({ id: task.id, body: { isDone: c === true } })
						}}
					/>
					<div className="flex flex-1 min-w-0 flex-col">
						<span className={`text-sm ${task.isDone ? 'line-through text-(--color-muted-foreground)' : 'text-(--color-foreground)'}`}>
							{task.title ?? task.description}
						</span>
						<span className="text-xs text-(--color-muted-foreground)">
							{t(`deals.tasks.types.${task.kind}`)}
							{task.dueDate ? ` • ${formatDueDate(task.dueDate)}` : ''}
						</span>
					</div>
					<IconButton
						variant="ghost"
						size="sm"
						type="button"
						onClick={() => setTaskToEdit(task)}
						aria-label={t('common.edit')}
					>
						<PencilIcon className="size-4" />
					</IconButton>
					<IconButton
						variant="ghost"
						size="sm"
						type="button"
						onClick={() => setDeleteTargetId(task.id)}
						aria-label={t('common.delete')}
					>
						<Trash2Icon className="size-4" />
					</IconButton>
				</div>
			))}

			<ConfirmDialog
				open={!!deleteTargetId}
				onOpenChange={(open) => { if (!open) setDeleteTargetId(null) }}
				title={t('deals.tasks.deleteDialog.title')}
				description={t('deals.tasks.deleteDialog.description')}
				confirmLabel={t('common.delete')}
				cancelLabel={t('common.cancel')}
				variant="destructive"
				onConfirm={confirmDelete}
			/>

			<Button
				variant="outline"
				size="sm"
				iconLeft={<PlusIcon className="size-4" />}
				className="self-start"
				onClick={() => setAdding(true)}
			>
				{t('deals.tasks.add')}
			</Button>

			<TaskFormDialog
				open={formOpen}
				onOpenChange={(open) => { if (!open) closeForm() }}
				task={taskToEdit}
				initialBinding={{ kind: 'DEAL', id: dealId }}
				lockBinding
			/>
		</div>
	)
}
